import type {SidebarLink} from '@github-ui/saved-views'
import {INBOX_SIDEBAR_LINK_ID} from '../constants/pulls-sidebar-links'
import {isOnInboxPath} from './is-on-inbox'

export function deriveSidebarLink({
  sidebarLinkId,
  basePath,
  savedViews,
  sidebarLinks,
  location,
}: {
  sidebarLinkId: string | undefined
  basePath: string
  savedViews: SidebarLink[]
  sidebarLinks: SidebarLink[]
  location: {pathname: string; search?: string}
}) {
  const {pathname} = location
  const isOnInbox = isOnInboxPath({pathname})

  if (sidebarLinkId) {
    const getItem = getItemById(sidebarLinkId, savedViews, sidebarLinks)
    if (getItem) {
      return getItem
    } else {
      const savedViewId = extractSavedViewId(pathname, basePath)
      if (savedViewId) {
        return savedViews.find((view: SidebarLink) => matchesSavedViewId(view, savedViewId))
      }
      return undefined
    }
  }

  // Inbox route treats filtered queries (for example repo filters) as part of Inbox.
  if (isOnInbox) {
    // When ?q= is present, /pulls will redirect to /pulls/search. Return undefined
    // so the header shows "Search" instead of briefly flashing "Inbox".
    if (location.search) {
      const searchParams = new URLSearchParams(location.search)
      if (searchParams.has('q')) {
        return undefined
      }
    }
    return getItemById(INBOX_SIDEBAR_LINK_ID, savedViews, sidebarLinks)
  }

  return undefined
}

// Saved views are addressed in the URL by their GraphQL global id, but numeric ids stay
// valid because they were the canonical form between the tanstack cutover and this
// change, so both are still bookmarked. See github/issues#23415.
function matchesSavedViewId(view: SidebarLink, id: string): boolean {
  return view.id.toString() === id || view.globalId === id
}

// Captures any single path segment rather than a specific id shape: the saved-view lookup
// below is the real filter, so tab paths like `/issues/assigned` still resolve to undefined.
// Matching a shape here would couple this to the backend's global id format, which differs
// between dotcom (`SSC_…`) and GHES (a legacy base64 relay id).
function extractSavedViewId(pathname: string, basePath: string): string | undefined {
  const normalized = pathname.replace(/\/+$/, '')
  const match = normalized.match(new RegExp(`^${basePath}/([^/]+)$`))
  return match?.[1]
}

export function getItemById(id: string, savedViews: SidebarLink[], links: SidebarLink[]) {
  if (!id) return undefined
  const allLinks = [...links, ...savedViews]
  if (id === INBOX_SIDEBAR_LINK_ID) {
    return allLinks.find(link => link.id === INBOX_SIDEBAR_LINK_ID)
  } else {
    return allLinks.find(item => {
      if (item.path) {
        return item.path === id
      } else {
        return matchesSavedViewId(item, id)
      }
    })
  }
}
